// src/utils/validation.js
/**
 * Cameroon Phone Number Validation Utility
 * Supports: MTN, Orange, Nextel, Camtel
 */

// Cameroon phone number regex patterns
const CAMEROON_PHONE_PATTERNS = {
  // MTN: 67X, 68X, 65X, 66X, 69X (9 digits total including prefix)
  MTN: /^[6](5[0-9]|6[0-9]|7[0-9]|8[0-9]|9[0-9])\d{6}$/,
  
  // Orange: 69X, 65X, 66X, 68X, 67X (9 digits total including prefix)
  ORANGE: /^[6](5[0-9]|6[0-9]|7[0-9]|8[0-9]|9[0-9])\d{6}$/,
  
  // Nextel: 62X, 63X (9 digits total including prefix)
  NEXTEL: /^6[23]\d{7}$/,
  
  // Camtel: 22X, 23X, 24X, 25X, 26X, 27X, 28X, 29X (9 digits total including prefix)
  CAMTEL_FIXED: /^2[2-9]\d{7}$/,
  
  // Camtel Mobile/3G/4G: 62X, 65X? (actually Camtel uses 62, sometimes 65)
  CAMTEL_MOBILE: /^6[25]\d{7}$/,
  
  // General Cameroon mobile (starts with 6)
  CAMEROON_MOBILE: /^6\d{8}$/,
  
  // General Cameroon fixed (starts with 2 or 3)
  CAMEROON_FIXED: /^[23]\d{8}$/,
  
  // Full Cameroon number (9 digits)
  CAMEROON_FULL: /^\d{9}$/
};

// Operator detection
const OPERATORS = {
  MTN: 'MTN',
  ORANGE: 'Orange',
  NEXTEL: 'Nextel',
  CAMTEL: 'Camtel',
  UNKNOWN: 'Unknown'
};

/**
 * Clean phone number by removing spaces, dashes, parentheses, and '+237' prefix
 */
export const cleanPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Convert to string and trim
  let cleaned = String(phone).trim();
  
  // Remove all non-digit characters except '+'
  cleaned = cleaned.replace(/[^\d+]/g, '');
  
  // Handle international format
  if (cleaned.startsWith('+237')) {
    cleaned = cleaned.substring(4);
  } else if (cleaned.startsWith('00237')) {
    cleaned = cleaned.substring(5);
  } else if (cleaned.startsWith('237')) {
    cleaned = cleaned.substring(3);
  }
  
  // Remove any remaining non-digits
  cleaned = cleaned.replace(/\D/g, '');
  
  // Ensure we have exactly 9 digits
  if (cleaned.length > 9) {
    cleaned = cleaned.slice(-9);
  }
  
  return cleaned;
};

/**
 * Format phone number for display (XX XX XX XX XX)
 */
export const formatPhoneNumber = (phone) => {
  const cleaned = cleanPhoneNumber(phone);
  if (cleaned.length !== 9) return phone;
  
  // Format as 6XX XX XX XX or 2XX XX XX XX
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 7)} ${cleaned.slice(7, 9)}`;
};

/**
 * Detect operator from phone number
 */
export const detectOperator = (phone) => {
  const cleaned = cleanPhoneNumber(phone);
  if (cleaned.length !== 9) return OPERATORS.UNKNOWN;
  
  const prefix = cleaned.substring(0, 3);
  
  // MTN: 67X, 68X, 65X, 66X, 69X
  if (['670', '671', '672', '673', '674', '675', '676', '677', '678', '679',
       '680', '681', '682', '683', '684', '685', '686', '687', '688', '689',
       '650', '651', '652', '653', '654', '655', '656', '657', '658', '659',
       '660', '661', '662', '663', '664', '665', '666', '667', '668', '669'].includes(prefix)) {
    return OPERATORS.MTN;
  }
  
  // Orange: 69X, 65X, 66X, 68X, 67X
  if (['690', '691', '692', '693', '694', '695', '696', '697', '698', '699',
       '670', '671', '672', '673', '674', '675', '676', '677', '678', '679'].includes(prefix)) {
    return OPERATORS.ORANGE;
  }
  
  // Nextel: 62X, 63X
  if (['620', '621', '622', '623', '624', '625', '626', '627', '628', '629',
       '630', '631', '632', '633', '634', '635', '636', '637', '638', '639'].includes(prefix)) {
    return OPERATORS.NEXTEL;
  }
  
  // Camtel: 22X, 23X, 24X, 25X, 26X, 27X, 28X, 29X (fixed) or 62X (mobile)
  if (prefix.startsWith('2')) {
    return OPERATORS.CAMTEL;
  }
  if (prefix.startsWith('62')) {
    return OPERATORS.CAMTEL;
  }
  
  return OPERATORS.UNKNOWN;
};

/**
 * Validate Cameroon phone number
 * Returns { isValid: boolean, error?: string, operator?: string }
 */
export const validatePhoneNumber = (phone, options = {}) => {
  const {
    required = true,
    allowInternational = true,
    minLength = 9,
    maxLength = 9,
    allowOperators = ['MTN', 'Orange', 'Nextel', 'Camtel']
  } = options;
  
  // Check if empty
  if (!phone || !String(phone).trim()) {
    return {
      isValid: !required,
      error: required ? 'Le numéro de téléphone est requis' : null,
      operator: null
    };
  }
  
  const cleaned = cleanPhoneNumber(phone);
  
  // Check length
  if (cleaned.length !== 9) {
    return {
      isValid: false,
      error: 'Le numéro doit contenir exactement 9 chiffres',
      operator: null
    };
  }
  
  // Check if it's a valid Cameroon number
  if (!CAMEROON_PHONE_PATTERNS.CAMEROON_FULL.test(cleaned)) {
    return {
      isValid: false,
      error: 'Numéro de téléphone camerounais invalide',
      operator: null
    };
  }
  
  // Check if it starts with valid prefix (6 or 2)
  if (!cleaned.startsWith('6') && !cleaned.startsWith('2')) {
    return {
      isValid: false,
      error: 'Le numéro doit commencer par 6 (mobile) ou 2 (fixe)',
      operator: null
    };
  }
  
  // Detect operator
  const operator = detectOperator(cleaned);
  
  // Check if operator is allowed
  if (operator !== OPERATORS.UNKNOWN && !allowOperators.includes(operator)) {
    return {
      isValid: false,
      error: `Les numéros ${operator} ne sont pas acceptés`,
      operator
    };
  }
  
  return {
    isValid: true,
    error: null,
    operator,
    formatted: formatPhoneNumber(cleaned),
    cleaned
  };
};

/**
 * Validation rules for forms
 */
export const phoneValidationRules = {
  required: (phone) => validatePhoneNumber(phone, { required: true }),
  optional: (phone) => validatePhoneNumber(phone, { required: false }),
  mtnOnly: (phone) => validatePhoneNumber(phone, { allowOperators: ['MTN'] }),
  orangeOnly: (phone) => validatePhoneNumber(phone, { allowOperators: ['Orange'] }),
  mobileOnly: (phone) => validatePhoneNumber(phone, { 
    allowOperators: ['MTN', 'Orange', 'Nextel', 'Camtel'] 
  })
};

/**
 * Email validation
 */
export const validateEmail = (email, required = true) => {
  if (!email || !String(email).trim()) {
    return {
      isValid: !required,
      error: required ? 'L\'email est requis' : null
    };
  }
  
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isValid = emailRegex.test(String(email).trim());
  
  return {
    isValid,
    error: isValid ? null : 'Format d\'email invalide'
  };
};

/**
 * Numeric validation
 */
export const validateNumeric = (value, options = {}) => {
  const {
    required = true,
    min,
    max,
    integer = false,
    positive = false
  } = options;
  
  if (!value && value !== 0) {
    return {
      isValid: !required,
      error: required ? 'Ce champ est requis' : null
    };
  }
  
  const num = Number(value);
  
  if (isNaN(num)) {
    return {
      isValid: false,
      error: 'Veuillez entrer un nombre valide'
    };
  }
  
  if (integer && !Number.isInteger(num)) {
    return {
      isValid: false,
      error: 'Veuillez entrer un nombre entier'
    };
  }
  
  if (positive && num <= 0) {
    return {
      isValid: false,
      error: 'Veuillez entrer un nombre positif'
    };
  }
  
  if (min !== undefined && num < min) {
    return {
      isValid: false,
      error: `La valeur doit être supérieure ou égale à ${min}`
    };
  }
  
  if (max !== undefined && num > max) {
    return {
      isValid: false,
      error: `La valeur doit être inférieure ou égale à ${max}`
    };
  }
  
  return {
    isValid: true,
    error: null,
    value: num
  };
};